import * as React from 'react';
import { useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Grid2';
import AccountManagement from 'components/AccountManagement/AccountManagement';
import AffiliateAuthorizationManager from 'components/AffiliateAuthorizationManager/AffiliateAuthorizationManager';
import { getUser } from 'actions/UserAction/UserActions';
import GenericSpinner from 'components/GenericSpinner/GenericSpinner';
import UserAccountCreation from 'modules/UserAccountCreation/UserAccountCreation';
import _ from 'lodash';

// Import Zustand store
import { useAppStore } from '../../stores';

interface IEditUserAccountInformationProps {
  closeParentModal: (open: boolean) => void;
  reloadUsers: () => void;
}

interface FormStateType {
  affPinNum: string;
  memberId: string;
  userRole: number | null;
}

const CreateOrUpdateUser: React.FC<IEditUserAccountInformationProps> = (props) => {
  // Zustand store
  const selectedUser = useAppStore((state) => state.user.selectedUserToEdit) as {
    user: {
      userId: number;
      firstName: string;
      lastName: string;
      username: string;
      memberId: string;
    };
    role: {
      roleId: number;
      roleName: string;
    };
    affPinNum?: string;
    Active: boolean;
  };
  const setSelectedUserToEdit = useAppStore((state) => state.setSelectedUserToEdit);

  const isNewUser = selectedUser.user.userId === 0;

  const displayMobile = useMediaQuery('(max-width:959px)');

  const [fetchUserRequestPending, setFetchUserRequestPending] = React.useState(true);
  const [individualGuid, setIndividualGuid] = React.useState<string | null>(null);

  const [selectedPropsValue, setSelectedPropsValue] = React.useState<string | number>(0);
  const [, setSelectedPropsText] = React.useState('');

  React.useEffect(() => {
    if (isNewUser) {
      setFetchUserRequestPending(false);
      return;
    }
    setFetchUserRequestPending(true);
    getUser(selectedUser.user.userId).then((response:
      { data?: {
        user: unknown;
        role: {
          roleId: number;
          roleName: string;
          requiresMemberId?: boolean
        };
        memberId?: string;
        affiliatePin?: string;
        active?: boolean;
        Active?: boolean;
      } }) => {
      if (response && response.data) {
        const { data } = response;

        const userData = data.user as {
          userId: number;
          firstName: string;
          lastName: string;
          username: string;
          memberId?: string;
          individualGuid?: string;
        };
        setIndividualGuid(userData.individualGuid ?? null);
        setSelectedUserToEdit({
          ...selectedUser,
          user: {
            userId: userData.userId,
            firstName: userData.firstName,
            lastName: userData.lastName,
            username: userData.username,
            memberId: userData.memberId || '',
          } as { 
            userId: number; 
            firstName: string; 
            lastName: string; 
            username: string; 
            memberId: string 
          },
          role: data.role,
          affPinNum: data.affiliatePin,
          Active: data.active !== undefined ? data.active : (data.Active !== undefined ? data.Active : (data.user as any).IsActive ?? false),
          memberId: data.memberId,
        });
        setSelectedRoleForDropdown(data.role.roleId);
        setSelectedPropsValue(data.role.roleId); // Fix: Update selectedPropsValue when user data loads
      }
      setFetchUserRequestPending(false);
    });
  }, []);

  const closeParentModal = (value: boolean) => {
    if (value !== undefined) {
      props.closeParentModal(value);
    }
  };

  const [selectedRoleForDropdown, setSelectedRoleForDropdown] = React.useState(0);

  // Get roles data from Zustand store at component level
  const roles = useAppStore((state) => state.rolesAndPermissions.roles) as Array<{
    roleId: number;
    roleName: string;
    requiresMemberId?: boolean;
    requiresAffiliatePin?: boolean;
  }>;

  const affiliatePinRequired = (selectedPropsValue: number) => {
    const currentRole = roles.find(role => role.roleId === Number(selectedPropsValue));
    return currentRole?.requiresAffiliatePin === true || (currentRole as any)?.RequiresAffiliatePin === true;
  };

  const memberIdRequired = () => {
    // Get the currently selected role ID
    const currentRoleId = isNewUser ? selectedRoleForDropdown : selectedPropsValue;
    
    // Find the current role and check its requiresMemberId field
    const currentRole = roles.find(role => role.roleId === Number(currentRoleId));
    return currentRole?.requiresMemberId === true;
  };

  const selectedRoleForDropdownOnChange = (e: { target: { value: unknown } }) => {
    setSelectedRoleForDropdown(Number(e.target.value));
  };

  const [formState, setFormState] = React.useState<FormStateType>({
    affPinNum: _.get(selectedUser, 'affPinNum') || '',
    memberId: _.get(selectedUser, 'memberId') || '',
    userRole: null,
  });

  React.useEffect(() => {
    setFormState({
      affPinNum: _.get(selectedUser, 'affPinNum') || '',
      memberId: _.get(selectedUser, 'memberId') || '',
      userRole: _.get(selectedUser, 'role.roleId'),
    });
  }, [selectedUser]);

  const handleFormChange = (ev: unknown) => {
    const event = ev as React.ChangeEvent<HTMLInputElement>;
    const state = { ...formState };
    (state as Record<string, unknown>)[event.target.name] = event.target.value;
    setFormState(state);
  };

  return (
    <Grid container className={`${!displayMobile && 'pb-3 pl-3 pr-3'}`}>
      {fetchUserRequestPending ? (
        <Grid container direction="column" alignItems="center" justifyContent="center">
          <Grid size={{ xs: 12, md: 12 }}>
            <GenericSpinner includeOverlay={false} padding={0} />
          </Grid>
        </Grid>
      ) : (
        <Grid container className="pt-3" style={{ overflow: 'visible', width: '100%' }}>
          {isNewUser ? (
            <UserAccountCreation
              forUserManagement={true}
              selectedUser={selectedUser}
              dropdownValue={selectedRoleForDropdown}
              dropDownOnChange={selectedRoleForDropdownOnChange}
              closeParentModal={closeParentModal}
              reloadUsers={props.reloadUsers}
              formState={formState}
              handleFormChange={handleFormChange}
              setSelectedPropsValue={setSelectedPropsValue}
              setSelectedPropsText={setSelectedPropsText}
              memberIdRequired={memberIdRequired}
              affiliatePinRequired={affiliatePinRequired}
            />
          ) : (
            <>
              <AccountManagement
                dropdownValue={selectedRoleForDropdown}
                dropDownOnChange={selectedRoleForDropdownOnChange}
                hideTitle={true}
                includeRoleDropdown={true}
                displayInModal={true}
                selectedUser={selectedUser}
                closeParentModal={closeParentModal}
                reloadUsers={props.reloadUsers}
                formState={formState}
                handleFormChange={handleFormChange}
                setSelectedPropsValue={setSelectedPropsValue}
                setSelectedPropsText={setSelectedPropsText}
                memberIdRequired={memberIdRequired}
                affiliatePinRequired={affiliatePinRequired}
              />
              {individualGuid && selectedRoleForDropdown === 16 /* Designee */ && (
                <AffiliateAuthorizationManager individualGuid={individualGuid} />
              )}
            </>
          )}
        </Grid>
      )}
    </Grid>
  );
};

export default CreateOrUpdateUser;
